/**
 * Posts-table column layout (§12.3).
 *
 * Visibility and order live in `ui_state['columns:posts']` — **global**, applying to every profile
 * and group (PRD) — read at mount and written back debounced 500 ms. Unknown or missing ids fall
 * back to the canonical order in `packages/shared/columns.ts`, which stays the single source of
 * truth for what a column even is.
 *
 * Since the redesign the Columns picker lives in the flat Posts header while the table renders in
 * `PostsMode` — two hook instances. The layout is therefore derived straight from the TanStack
 * Query cache and edits go back through `setQueryData`, so every instance always sees one layout.
 *
 * The blob sanitizer itself — including the pin that seats `preview` first on every read and write
 * — lives in `viewState.ts`, where the node suite can reach it.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  POSTS_COLUMN_IDS,
  POSTS_COLUMNS_UI_STATE_KEY,
  type PostsColumnId,
  type PostsColumnsUiState,
} from '@lw/shared';
import { getUiState, putUiState } from '../../../api/uiState';
import { QK } from '../../../api/queryKeys';
import { useDebouncedCallback } from '../../../lib/hooks';
import {
  DEFAULT_COLUMN_LAYOUT,
  PINNED_POSTS_COLUMN,
  pinColumnFirst,
  sanitizeColumnLayout,
  type ColumnLayout,
} from '../viewState';

const WRITE_DEBOUNCE_MS = 500;

export type ColumnLayoutHandle = {
  ready: boolean;
  order: PostsColumnId[];
  hidden: Set<PostsColumnId>;
  /** The frozen first column, or `null` for a grid that pins nothing. */
  pinned: PostsColumnId | null;
  setOrder: (next: PostsColumnId[]) => void;
  toggle: (id: PostsColumnId) => void;
  reset: () => void;
};

export function useColumnLayout(): ColumnLayoutHandle {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: QK.uiState(POSTS_COLUMNS_UI_STATE_KEY),
    queryFn: ({ signal }) => getUiState<unknown>(POSTS_COLUMNS_UI_STATE_KEY, signal),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // A failed read falls back to the canonical layout rather than blocking the table for ever —
  // seeding the cache keeps every hook instance on the same fallback.
  useEffect(() => {
    if (!query.isError) return;
    const key = QK.uiState(POSTS_COLUMNS_UI_STATE_KEY);
    if (queryClient.getQueryData(key) !== undefined) return;
    const payload: PostsColumnsUiState = {
      order: DEFAULT_COLUMN_LAYOUT.order,
      hidden: DEFAULT_COLUMN_LAYOUT.hidden,
    };
    queryClient.setQueryData(key, payload);
  }, [query.isError, queryClient]);

  const layout = useMemo(
    () => (query.data === undefined ? null : sanitizeColumnLayout(query.data)),
    [query.data],
  );

  const persist = useDebouncedCallback((next: ColumnLayout) => {
    const payload: PostsColumnsUiState = { order: next.order, hidden: next.hidden };
    void putUiState(POSTS_COLUMNS_UI_STATE_KEY, payload).catch(() => {
      // Layout persistence is best-effort — the table still works from the cached state.
    });
  }, WRITE_DEBOUNCE_MS);

  // Every edit reads the cache, not component state, so a change made in one instance (the header
  // menu) is immediately the base for the next change in another (a header drag in the table).
  const commit = useCallback(
    (updater: (current: ColumnLayout) => ColumnLayout) => {
      const key = QK.uiState(POSTS_COLUMNS_UI_STATE_KEY);
      const next = updater(sanitizeColumnLayout(queryClient.getQueryData(key)));
      const payload: PostsColumnsUiState = { order: next.order, hidden: next.hidden };
      queryClient.setQueryData(key, payload);
      persist(next);
    },
    [persist, queryClient],
  );

  const setOrder = useCallback(
    (next: PostsColumnId[]) => {
      const ordered = pinColumnFirst(
        [...next, ...POSTS_COLUMN_IDS.filter((id) => !next.includes(id))],
        PINNED_POSTS_COLUMN,
      );
      commit((current) => ({ ...current, order: ordered }));
    },
    [commit],
  );

  const toggle = useCallback(
    (id: PostsColumnId) => {
      commit((current) => ({
        ...current,
        hidden: current.hidden.includes(id)
          ? current.hidden.filter((entry) => entry !== id)
          : [...current.hidden, id],
      }));
    },
    [commit],
  );

  const reset = useCallback(() => commit(() => ({ ...DEFAULT_COLUMN_LAYOUT })), [commit]);

  const effective = layout ?? DEFAULT_COLUMN_LAYOUT;

  return {
    ready: layout !== null,
    order: effective.order,
    hidden: new Set(effective.hidden),
    pinned: PINNED_POSTS_COLUMN,
    setOrder,
    toggle,
    reset,
  };
}
